import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SignalIcon, BoltIcon, UserGroupIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import LiveCallsSection from '../../components/LiveCalls/LiveCallsSection';
import { getAccessTokenTtlMs, getSseAccessToken } from '../../utils/sseAuthToken';
import { api } from '../../services/api';

interface AutoDialAgent {
  id: string;
  name: string;
  email: string;
  /** Calling identity shown in Agents column (replaces name+email). */
  aiName?: string;
  /** Campaign associated with this agent. */
  campaignName?: string;
  isOnline: boolean;
  isAvailable: boolean;
  maxCalls: number;
  activeCalls: number;
  ringingCalls: number;
  bridgedCalls: number;
  openSlots: number;
  callStatus: 'in-call' | 'ringing' | 'idle' | 'offline' | 'wrap-up';
  /** Seconds in wrap-up awaiting disposition (when callStatus is wrap-up). */
  wrapUpSeconds?: number;
  calls: Array<{
    callControlId: string;
    leadName?: string;
    leadPhone?: string;
    status: string;
    campaignName?: string;
  }>;
  dailyCounters: {
    totalCallsDialed: number;
    answeredCalls: number;
    interested: number;
    followUps: number;
    appIn: number;
    appOut: number;
  };
  /** Day avg gap between dispositioned connects (not dial cadence). */
  avgWaitBetweenCallsSeconds: number;
  /** Live seconds idle since last dispositioned connect (primary “waiting now”). */
  currentIdleWaitSeconds: number;
  /** Seconds since last dispositioned connect (outcome set). 0 = none. */
  secondsSinceLastDispositionedConnect: number;
}

interface AutoDialCampaignPacing {
  dialRatio: number;
  targetActiveCalls: number;
  activeCalls: number;
  deficit: number;
  availableAgents: number;
  isPaused: boolean;
  pausedUntil: string | null;
  fillPercent: number;
  health: 'normal' | 'slowed' | 'paused';
  predictiveCancelledRate: number;
  fairShareAllowance?: number | null;
  performanceWeight?: number | null;
  fairSharePeerCount?: number | null;
}

interface AutoDialDashboardData {
  campaigns: Array<{
    id: string;
    name: string;
    status: string;
    callsPerAgent: number;
    dialRatio: number;
    pacing: AutoDialCampaignPacing;
  }>;
  agents: AutoDialAgent[];
  leadStatusCounts: Record<string, number>;
  stats: {
    totalActiveCalls: number;
    totalOpenSlots: number;
    onlineAgents: number;
    activeCampaigns: number;
    totalDialedToday: number;
    uniqueLeadsDialedToday?: number;
    amdHumanToday?: number;
    pickupsToday?: number;
    connectedToday: number;
    noAnswerToday: number;
    userBusyToday: number;
    voicemailsFilteredToday: number;
    failedToday: number;
    cancelledToday: number;
    avgWaitToNextCallSeconds: number;
    connectedAvgDuration: number;
    targetActiveCalls: number;
    pacingDeficit: number;
    pipelineFillPercent: number;
    pacingHealth: 'normal' | 'slowed' | 'paused';
    predictiveCancelledRate: number;
    predictiveCancelledRatePct: number;
    wrapUpAgents: number;
    avgBridgeDelaySeconds: number;
  };
  pacingControl?: {
    mode: string;
    avgAgentWaitSeconds: number;
    waitBand: string;
    longestAgentWaitSeconds?: number;
    longestWaitingAgentName?: string | null;
    merchantConnectRate: number;
    connectSampleSize: number;
    predictiveCancelledRate: number;
    dialRatio: number;
    effectiveTargetCalls: number;
    lastDecisionReason: string | null;
    lastDecisionReasonLabel?: string | null;
    lastDecisionAt: string | null;
    decisionLog: Array<{ at: string; reason: string; fromRatio?: number; toRatio: number; shadow?: boolean }>;
    breakerOpen: boolean;
    pausedUntil: string | null;
    executeSkippedPausedToday: number;
    shadowSuggestedRatio?: number;
    agentTable?: {
      availableAgents: number;
      baseTarget: number;
      offset: number;
      effectiveTarget: number;
      capTarget: number;
      floorTarget: number;
    } | null;
    stabilizer: { cooldownActive: boolean; scalerClamped: boolean };
    cidAvailableByCarrier: Record<string, number>;
    cidOnCooldownByCarrier?: Record<string, number>;
    cidLoadByAreaCode?: Array<{
      areaCode: string;
      owned: number;
      healthy: number;
      dialsToday: number;
      dialsPerNumber: number;
    }>;
    cidConnectPriority?: {
      lastUsedNumber: string | null;
      counts: { high: number; mid: number; low: number; warmup: number; unknown: number };
      numbers: Array<{
        number: string;
        areaCode: string | null;
        connectPct: number | null;
        band: 'high' | 'mid' | 'low' | 'warmup' | 'unknown';
        slots: number;
        health: string;
        lastUsed: boolean;
      }>;
    };
    cidMatchTierCountsToday?: Record<string, number>;
    missingCallerIdDeferredToday: number;
    funnel?: { ringing: number; queued: number; bridged: number; availableAgents: number };
    cidDialShareByCarrier?: Record<string, number>;
    spamFlaggedCount?: number;
    topCidsByConnects?: Array<{ number: string; connects: number }>;
    topCidsByFails?: Array<{ number: string; fails: number }>;
    eligibleLeadsRemaining?: number;
    deferredMissingCidLeads?: number;
    agentsWithReservedDials?: number;
    dialWindow?: {
      enabled: boolean;
      timezoneSource: string;
      activeZones: string[];
      etWaveZones: string[] | null;
      nowEt: string;
      eligibleLeadsInWindow: number;
      eligibleLeadsTotal: number;
      idleReason: string;
      idleReasonLabel: string;
    };
    alerts: Array<{ level: 'info' | 'warn' | 'critical'; code: string; message: string }>;
    waitAlert?: boolean;
    waitAlertSeconds?: number;
  };
}

interface AutoDialActivityEvent {
  id: string;
  at: string;
  level: 'info' | 'success' | 'warning' | 'error';
  type: string;
  message: string;
  meta?: Record<string, unknown>;
  campaignId?: string;
  campaignName?: string;
  leadName?: string;
  leadPhone?: string;
  agentName?: string;
}

const ACTIVITY_LEVEL_STYLES: Record<AutoDialActivityEvent['level'], string> = {
  info: 'text-foreground',
  success: 'text-green-700 dark:text-green-400',
  warning: 'text-amber-700 dark:text-amber-400',
  error: 'text-red-700 dark:text-red-400',
};

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  batch_scheduled: 'Schedule',
  dial_skipped: 'Skip',
  dial_initiated: 'Dial',
  dial_deferred: 'Defer',
  customer_ringing: 'Ring',
  customer_answered: 'Answered',
  human_detected: 'Human',
  agent_transfer_started: 'Transfer',
  agent_fallback: 'Fallback',
  agent_connected: 'Connected',
  no_agents_available: 'No agents',
  amd_machine: 'Voicemail',
  call_ended: 'Ended',
};

/** Same buckets/colors as the manual Live Monitor's Lead Status Breakdown. */
const LEAD_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  // Rollup "Answered" removed — it duplicated Hang Up when hangups dominated.
  interested: { label: 'Interested', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  not_interested: { label: 'Not Interested', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  follow_up: { label: 'Follow Up', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  no_answer: { label: 'No Answer', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
  busy: { label: 'Busy', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  unreachable: { label: 'Unreachable', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  do_not_call: { label: 'Do Not Call', color: 'bg-red-200 text-red-900 dark:bg-red-900/40 dark:text-red-300' },
  out_of_business: { label: 'Out of Business', color: 'bg-gray-200 text-gray-900 dark:bg-gray-800/40 dark:text-gray-300' },
  wrong_number: { label: 'Wrong Number', color: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400' },
  unqualified: { label: 'Unqualified', color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400' },
  gatekeeper: { label: 'Gatekeeper', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400' },
  app_in: { label: 'App In', color: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400' },
  app_out: { label: 'App Out', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
  spanish: { label: 'Spanish', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400' },
  hangup: { label: 'Hang Up', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  operator: { label: 'Operator', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' },
  voicemail: { label: 'Voicemail', color: 'bg-orange-200 text-orange-900 dark:bg-orange-900/40 dark:text-orange-300' },
  cancelled: { label: 'Predictive Cancelled', color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400' },
};

function formatSeconds(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatWaitTime(sec?: number): string {
  const safe = Math.max(Math.floor(sec || 0), 0);
  if (safe >= 3600) return `${Math.floor(safe / 3600)}h ${Math.floor((safe % 3600) / 60)}m`;
  if (safe >= 60) return `${Math.floor(safe / 60)}m ${safe % 60}s`;
  return `${safe}s`;
}

const PACING_HEALTH_STYLES: Record<
  AutoDialDashboardData['stats']['pacingHealth'],
  { label: string; className: string }
> = {
  normal: { label: 'Normal', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  slowed: { label: 'Slowed', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  paused: { label: 'Paused', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

function pcRateColor(pct: number): string {
  if (pct >= 10) return 'text-red-600 dark:text-red-400';
  if (pct >= 5) return 'text-amber-600 dark:text-amber-400';
  if (pct >= 3) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
}

function etCalendarDate(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

const AutoDialMonitorPage: React.FC = () => {
  const today = etCalendarDate();
  const [data, setData] = useState<AutoDialDashboardData | null>(null);
  const [activityLog, setActivityLog] = useState<AutoDialActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const eventSourceRef = useRef<EventSource | null>(null);
  const closedRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  // Client request: PC / cancel rate should only refresh every 10 minutes.
  // SSE still pushes full dashboards every few seconds for agent slots — hold PC fields.
  const PC_REFRESH_MS = 10 * 60_000;
  const pcHoldRef = useRef<{
    at: number;
    predictiveCancelledRate: number | null;
    statsPc: number | null;
    statsPcPct: number | null;
    cancelledToday: number | null;
    pacingHealth: AutoDialDashboardData['stats']['pacingHealth'] | null;
    pcAlerts: NonNullable<AutoDialDashboardData['pacingControl']>['alerts'];
    campaignPc: Record<string, { rate: number; health: string }>;
  } | null>(null);

  const isTodayRange = fromDate === today && toDate === today;
  const rangeLabel = isTodayRange ? 'today' : `${fromDate} – ${toDate}`;

  const streamFilters = useMemo(
    () => ({ selectedCampaign, selectedAgent, fromDate, toDate }),
    [selectedCampaign, selectedAgent, fromDate, toDate]
  );

  useEffect(() => {
    if (!selectedCampaign || !data?.campaigns) return;
    const stillActive = data.campaigns.some(
      (c) => c.id === selectedCampaign && String(c.status).toLowerCase() === 'active'
    );
    if (!stillActive) setSelectedCampaign('');
  }, [data?.campaigns, selectedCampaign]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('EventSource' in window)) return;

    closedRef.current = false;
    pcHoldRef.current = null;
    setLoading(true);
    let connectGeneration = 0;

    const applyDashboard = (incoming: AutoDialDashboardData, allowPcRefresh: boolean) => {
      const now = Date.now();
      const hold = pcHoldRef.current;
      const shouldRefreshPc = allowPcRefresh || !hold || now - hold.at >= PC_REFRESH_MS;

      if (shouldRefreshPc) {
        const pcAlerts = (incoming.pacingControl?.alerts || []).filter((a) =>
          String(a.code || '').startsWith('pc_')
        );
        pcHoldRef.current = {
          at: now,
          predictiveCancelledRate: incoming.pacingControl?.predictiveCancelledRate ?? null,
          statsPc: incoming.stats?.predictiveCancelledRate ?? null,
          statsPcPct: incoming.stats?.predictiveCancelledRatePct ?? null,
          cancelledToday: incoming.stats?.cancelledToday ?? null,
          pacingHealth: incoming.stats?.pacingHealth ?? null,
          pcAlerts,
          campaignPc: Object.fromEntries(
            (incoming.campaigns || []).map((c) => [
              c.id,
              {
                rate: Number(c.pacing?.predictiveCancelledRate ?? 0),
                health: String(c.pacing?.health ?? 'normal'),
              },
            ])
          ),
        };
        setData(incoming);
        return;
      }


      const nonPcAlerts = (incoming.pacingControl?.alerts || []).filter(
        (a) => !String(a.code || '').startsWith('pc_')
      );
      setData({
        ...incoming,
        stats: incoming.stats
          ? {
              ...incoming.stats,
              predictiveCancelledRate: hold.statsPc ?? incoming.stats.predictiveCancelledRate,
              predictiveCancelledRatePct: hold.statsPcPct ?? incoming.stats.predictiveCancelledRatePct,
              cancelledToday: hold.cancelledToday ?? incoming.stats.cancelledToday,
              pacingHealth: hold.pacingHealth ?? incoming.stats.pacingHealth,
            }
          : incoming.stats,
        pacingControl: incoming.pacingControl
          ? {
              ...incoming.pacingControl,
              predictiveCancelledRate:
                hold.predictiveCancelledRate ?? incoming.pacingControl.predictiveCancelledRate,
              alerts: [...hold.pcAlerts, ...nonPcAlerts],
            }
          : incoming.pacingControl,
        campaigns: (incoming.campaigns || []).map((c) => {
          const frozen = hold.campaignPc[c.id];
          if (!c.pacing || !frozen) return c;
          return {
            ...c,
            pacing: {
              ...c.pacing,
              predictiveCancelledRate: frozen.rate,
              health: frozen.health as typeof c.pacing.health,
            },
          };
        }),
      });
    };

    const buildStreamUrl = (token: string) => {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const fullUrl = baseUrl.startsWith('http')
        ? `${baseUrl}/admin/auto-dialer-dashboard/stream`
        : `${window.location.origin}${baseUrl}/admin/auto-dialer-dashboard/stream`;
      const url = new URL(fullUrl);
      url.searchParams.set('token', token);
      if (streamFilters.selectedCampaign) {
        url.searchParams.set('campaignId', streamFilters.selectedCampaign);
      }
      if (streamFilters.selectedAgent) {
        url.searchParams.set('agentId', streamFilters.selectedAgent);
      }
      if (streamFilters.fromDate) {
        url.searchParams.set('startDate', `${streamFilters.fromDate}T00:00:00.000Z`);
      }
      if (streamFilters.toDate) {
        url.searchParams.set('endDate', `${streamFilters.toDate}T23:59:59.999Z`);
      }
      return url.toString();
    };

    const fetchSnapshot = async () => {
      try {
        const params: Record<string, string> = {};
        if (streamFilters.selectedCampaign) params.campaignId = streamFilters.selectedCampaign;
        if (streamFilters.selectedAgent) params.agentId = streamFilters.selectedAgent;
        if (streamFilters.fromDate) params.startDate = `${streamFilters.fromDate}T00:00:00.000Z`;
        if (streamFilters.toDate) params.endDate = `${streamFilters.toDate}T23:59:59.999Z`;
        const res = await api.get('/admin/auto-dialer-dashboard', { params });
        const payload = res.data?.data as AutoDialDashboardData | undefined;
        if (payload && !closedRef.current) {
          applyDashboard(payload, true);
          setLoading(false);
        }
      } catch {
        // ignore transient snapshot errors; SSE/reconnect will retry
      }
    };

    const cleanup = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };

    const scheduleReconnect = () => {
      if (closedRef.current || reconnectTimerRef.current) return;
      if (document.visibilityState !== 'visible') return;
      reconnectAttemptsRef.current += 1;
      const delay = Math.min(30_000, 1000 * 2 ** (reconnectAttemptsRef.current - 1));
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        void connect();
      }, delay);
    };

    const connect = async () => {
      const generation = ++connectGeneration;
      cleanup();
      if (closedRef.current) return;

      if (document.visibilityState !== 'visible') {
        scheduleReconnect();
        return;
      }

      try {
        const token = await getSseAccessToken();
        if (closedRef.current || generation !== connectGeneration) return;

        if (!token) {
          scheduleReconnect();
          return;
        }

        const es = new EventSource(buildStreamUrl(token));
        if (closedRef.current || generation !== connectGeneration) {
          es.close();
          return;
        }
        eventSourceRef.current = es;

        const handlePayload = (raw: string) => {
          try {
            applyDashboard(JSON.parse(raw) as AutoDialDashboardData, false);
          } catch {
            // ignore
          } finally {
            setLoading(false);
          }
        };

        es.addEventListener('auto-dial-dashboard', (event) => {
          handlePayload((event as MessageEvent).data || '');
        });

        es.addEventListener('auto-dial-activity', (event) => {
          try {
            const payload = JSON.parse((event as MessageEvent).data || '{}') as {
              type?: string;
              events?: AutoDialActivityEvent[];
              event?: AutoDialActivityEvent;
            };
            if (payload.type === 'activity_snapshot' && Array.isArray(payload.events)) {
              setActivityLog(payload.events);
            } else if (payload.type === 'activity_event' && payload.event) {
              setActivityLog((prev) => [payload.event!, ...prev].slice(0, 100));
            }
          } catch {
            // ignore
          }
        });

        es.addEventListener('ping', () => {});

        es.onopen = () => {
          reconnectAttemptsRef.current = 0;
        };

        es.onerror = () => {
          if (closedRef.current || generation !== connectGeneration) return;
          // Only reconnect after the socket is actually closed (avoid CONNECTING flicker loops).
          if (es.readyState !== EventSource.CLOSED) return;
          es.close();
          if (eventSourceRef.current === es) eventSourceRef.current = null;
          scheduleReconnect();
        };
      } catch {
        if (!closedRef.current && generation === connectGeneration) scheduleReconnect();
      }
    };

    const handleVisibility = () => {
      if (closedRef.current) return;
      if (document.visibilityState === 'visible' && !eventSourceRef.current) {
        void connect();
      }
    };

    void fetchSnapshot();
    void connect();
    document.addEventListener('visibilitychange', handleVisibility);

    // Keep PC/cancel cards fresh while idle (SSE only pushes on call activity).
    // Client request: 10 min — 1 min felt too noisy.
    const refreshInterval = window.setInterval(() => {
      if (closedRef.current) return;
      if (document.visibilityState !== 'visible') return;
      void fetchSnapshot();
    }, 10 * 60_000);

    return () => {
      closedRef.current = true;
      connectGeneration += 1;
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(refreshInterval);
      cleanup();
    };
  }, [streamFilters]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const agents = data?.agents || [];
  const campaigns = data?.campaigns || [];
  const activeCampaigns = campaigns.filter((c) => String(c.status).toLowerCase() === 'active');
  const stats = data?.stats;
  const leadCounts = data?.leadStatusCounts || {};
  const pacingControl = data?.pacingControl;
  const pacingHealthStyle = stats ? PACING_HEALTH_STYLES[stats.pacingHealth] : PACING_HEALTH_STYLES.normal;

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {pacingControl && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            pacingControl.mode === 'shadow' || pacingControl.mode === 'canary'
              ? 'border-yellow-300 bg-yellow-50 text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-200'
              : pacingControl.mode === 'on'
                ? 'border-green-300 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950/40 dark:text-green-200'
                : 'border-border bg-muted/40 text-muted-foreground'
          }`}
        >
          <strong>Pacing mode: {pacingControl.mode}</strong>
          {pacingControl.shadowSuggestedRatio != null && (
            <span className="ml-2">
              Suggested ratio {pacingControl.shadowSuggestedRatio} (live {pacingControl.dialRatio})
            </span>
          )}
          {pacingControl.mode === 'off' && (
            <span className="ml-2">Legacy Phase-4 brain</span>
          )}
        </div>
      )}
      {pacingControl?.alerts && pacingControl.alerts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {pacingControl.alerts.map((a) => (
            <span
              key={a.code}
              className={`inline-flex text-xs font-medium px-2 py-1 rounded-full ${
                a.level === 'critical'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                  : a.level === 'warn'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                    : 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
              }`}
            >
              {a.message}
            </span>
          ))}
        </div>
      )}
      {stats && stats.pacingHealth !== 'normal' && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            stats.pacingHealth === 'paused'
              ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200'
              : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200'
          }`}
        >
          <strong>Pacing {pacingHealthStyle.label}:</strong>{' '}
          {stats.pacingHealth === 'paused'
            ? 'Emergency slowdown active — new dials paused until timer expires.'
            : `Predictive cancel rate ${stats.predictiveCancelledRatePct}% exceeds 5% target — dial ratio is being reduced.`}
        </div>
      )}
      <div className="space-y-3">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <BoltIcon className="h-7 w-7 text-primary" />
          Auto Dial Live Monitor
        </h1>
        <p className="text-sm text-muted-foreground">
          Agent slots update via throttled SSE; activity log streams instantly on each dial event (no DB polling).
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {activeCampaigns.length > 1 && (
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">All Active Campaigns</option>
              {activeCampaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">All Agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
          <Button
            variant="outlined"
            size="sm"
            onClick={() => {
              setFromDate(today);
              setToDate(today);
            }}
          >
            Today
          </Button>
        </div>
      </div>

      {pacingControl && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card className={pacingControl.waitAlert ? 'border-red-500 ring-1 ring-red-500/40' : undefined}>
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${pacingControl.waitAlert ? 'text-red-600 dark:text-red-400' : ''}`}>
                {formatWaitTime(pacingControl.avgAgentWaitSeconds)}
              </div>
              <div className="text-sm text-muted-foreground">Waiting now (avg)</div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                since last disposition · {pacingControl.waitBand.replace(/_/g, ' ')}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{Math.round(pacingControl.merchantConnectRate * 1000) / 10}%</div>
              <div className="text-sm text-muted-foreground">Connect rate</div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                dispositioned connects · last {pacingControl.connectSampleSize || 100}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{Math.round(pacingControl.predictiveCancelledRate * 1000) / 10}%</div>
              <div className="text-sm text-muted-foreground">PC rate</div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                no-agent cancels · last 100 in date range · target &lt;3%
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{pacingControl.dialRatio}</div>
              <div className="text-sm text-muted-foreground">Dial ratio</div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5 truncate" title={pacingControl.lastDecisionReasonLabel || pacingControl.lastDecisionReason || ''}>
                {pacingControl.lastDecisionReasonLabel || pacingControl.lastDecisionReason || '—'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${pacingControl.breakerOpen ? 'text-red-600' : 'text-green-600'}`}>
                {pacingControl.breakerOpen ? 'OPEN' : 'OK'}
              </div>
              <div className="text-sm text-muted-foreground">Breaker</div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                {pacingControl.pausedUntil ? `Until ${new Date(pacingControl.pausedUntil).toLocaleTimeString()}` : 'Not paused'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{pacingControl.missingCallerIdDeferredToday}</div>
              <div className="text-sm text-muted-foreground">Missing CID</div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                Skip paused {pacingControl.executeSkippedPausedToday}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{pacingControl.effectiveTargetCalls}</div>
              <div className="text-sm text-muted-foreground">Effective target</div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                {pacingControl.agentTable
                  ? `Table ${pacingControl.agentTable.baseTarget} @ ${pacingControl.agentTable.availableAgents} agents ${
                      pacingControl.agentTable.offset >= 0 ? '+' : '−'
                    }${Math.abs(pacingControl.agentTable.offset)} = ${pacingControl.agentTable.effectiveTarget} (cap ${pacingControl.agentTable.capTarget})`
                  : 'After connect scaler'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-1">Stabilizer</div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>Cooldown: {pacingControl.stabilizer.cooldownActive ? 'active' : 'off'}</div>
                <div>Scaler clamp: {pacingControl.stabilizer.scalerClamped ? 'yes' : 'no'}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {pacingControl && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-1">Funnel</div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>Available: {pacingControl.funnel?.availableAgents ?? '—'}</div>
                <div>Queued: {pacingControl.funnel?.queued ?? '—'}</div>
                <div>Ringing: {pacingControl.funnel?.ringing ?? '—'}</div>
                <div>Bridged: {pacingControl.funnel?.bridged ?? '—'}</div>
              </div>
            </CardContent>
          </Card>
          <Card className={pacingControl.waitAlert ? 'border-red-500 ring-1 ring-red-500/40' : undefined}>
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-1">Longest waiting now</div>
              <div className={`text-2xl font-bold ${pacingControl.waitAlert ? 'text-red-600 dark:text-red-400' : ''}`}>
                {formatWaitTime(pacingControl.longestAgentWaitSeconds ?? 0)}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                {pacingControl.longestWaitingAgentName || '—'}
                {pacingControl.waitAlert
                  ? ` · red after ${pacingControl.waitAlertSeconds ?? 120}s`
                  : ''}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-1">CID match tiers</div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>Area+carrier: {pacingControl.cidMatchTierCountsToday?.carrier_area ?? 0}</div>
                <div>Carrier: {pacingControl.cidMatchTierCountsToday?.carrier ?? 0}</div>
                <div>All: {pacingControl.cidMatchTierCountsToday?.all ?? 0}</div>
                <div>Missing: {pacingControl.cidMatchTierCountsToday?.missing ?? 0}</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-1">CID pool / cooldown</div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {['verizon', 'att', 'tmobile', 'all'].map((k) => (
                  <div key={k}>
                    {k}: {pacingControl.cidAvailableByCarrier?.[k] ?? 0} avail
                    {pacingControl.cidOnCooldownByCarrier?.[k]
                      ? ` / ${pacingControl.cidOnCooldownByCarrier[k]} CD`
                      : ''}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          {(pacingControl.cidLoadByAreaCode?.length ?? 0) > 0 && (
            <Card className="sm:col-span-2 lg:col-span-3">
              <CardContent className="p-4">
                <div className="text-sm font-medium mb-1">Caller ID load by area code</div>
                <div className="text-[11px] text-muted-foreground/80 mb-2">
                  Dials today vs owned auto numbers. Auto-dial prefers local area code but still
                  rotates other lines so they warm up.
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-muted-foreground">
                      <tr className="text-left">
                        <th className="py-1 pr-3 font-medium">Area</th>
                        <th className="py-1 pr-3 font-medium">Numbers</th>
                        <th className="py-1 pr-3 font-medium">Healthy</th>
                        <th className="py-1 pr-3 font-medium">Dials today</th>
                        <th className="py-1 font-medium">Per number</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pacingControl.cidLoadByAreaCode!.map((row) => {
                        const tone =
                          row.dialsPerNumber >= 100
                            ? 'text-red-600 dark:text-red-400 font-semibold'
                            : row.dialsPerNumber >= 60
                              ? 'text-amber-600 dark:text-amber-400 font-medium'
                              : '';
                        return (
                          <tr key={row.areaCode} className="border-t border-border/50">
                            <td className="py-1 pr-3">{row.areaCode}</td>
                            <td className="py-1 pr-3">{row.owned}</td>
                            <td className="py-1 pr-3">{row.healthy}</td>
                            <td className="py-1 pr-3">{row.dialsToday}</td>
                            <td className={`py-1 ${tone}`}>{row.dialsPerNumber}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      {pacingControl?.cidConnectPriority && (
        <Card>
          <CardContent className="p-4">
            <div className="text-sm font-medium mb-1">Caller ID connect-rate priority</div>
            <p className="text-[11px] text-muted-foreground/80 mb-2">
              Each from-number&apos;s connect rate: ≥10% gets 3 rotation slots, 6–10% / warm-up get 2,
              ≤6% gets 1. Consecutive calls skip the last-used CID.
            </p>
            <div className="flex flex-wrap gap-2 text-[11px] mb-3">
              <span className="rounded-md bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5">
                ≥10% {pacingControl.cidConnectPriority.counts.high}
              </span>
              <span className="rounded-md bg-muted text-foreground px-2 py-0.5">
                Mid {pacingControl.cidConnectPriority.counts.mid}
              </span>
              <span className="rounded-md bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5">
                ≤6% {pacingControl.cidConnectPriority.counts.low}
              </span>
              <span className="rounded-md bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400 px-2 py-0.5">
                Warm-up {pacingControl.cidConnectPriority.counts.warmup}
              </span>
              {(pacingControl.cidConnectPriority.counts.unknown || 0) > 0 && (
                <span className="rounded-md bg-muted text-muted-foreground px-2 py-0.5">
                  No rate {pacingControl.cidConnectPriority.counts.unknown}
                </span>
              )}
            </div>
            {pacingControl.cidConnectPriority.numbers.length === 0 ? (
              <p className="text-xs text-muted-foreground">No active auto caller IDs to score yet.</p>
            ) : (
              <div className="overflow-x-auto max-h-56 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground sticky top-0 bg-card">
                    <tr className="text-left">
                      <th className="py-1 pr-3 font-medium">Number</th>
                      <th className="py-1 pr-3 font-medium">Area</th>
                      <th className="py-1 pr-3 font-medium">Connect</th>
                      <th className="py-1 pr-3 font-medium">Priority</th>
                      <th className="py-1 font-medium">Slots</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pacingControl.cidConnectPriority.numbers.map((row) => {
                      const bandLabel =
                        row.band === 'high'
                          ? 'High'
                          : row.band === 'low'
                            ? 'Low'
                            : row.band === 'warmup'
                              ? 'Warm-up'
                              : row.band === 'unknown'
                                ? '—'
                                : 'Mid';
                      const bandClass =
                        row.band === 'high'
                          ? 'text-emerald-700 dark:text-emerald-400 font-medium'
                          : row.band === 'low'
                            ? 'text-amber-700 dark:text-amber-400'
                            : row.band === 'warmup'
                              ? 'text-sky-700 dark:text-sky-400'
                              : 'text-muted-foreground';
                      const tail = row.number.replace(/\D/g, '').slice(-4);
                      return (
                        <tr key={row.number} className="border-t border-border/50">
                          <td className="py-1 pr-3 font-mono">
                            …{tail || row.number}
                            {row.lastUsed ? (
                              <span className="ml-1 text-[10px] text-muted-foreground">(last)</span>
                            ) : null}
                          </td>
                          <td className="py-1 pr-3">{row.areaCode || '—'}</td>
                          <td className="py-1 pr-3">
                            {row.connectPct == null ? '—' : `${row.connectPct.toFixed(1)}%`}
                          </td>
                          <td className={`py-1 pr-3 ${bandClass}`}>{bandLabel}</td>
                          <td className="py-1">{row.slots}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {pacingControl && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-1">Dial share %</div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {['verizon', 'att', 'tmobile', 'all'].map((k) => (
                  <div key={k}>
                    {k}: {pacingControl.cidDialShareByCarrier?.[k] ?? 0}%
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{pacingControl.spamFlaggedCount ?? 0}</div>
              <div className="text-sm text-muted-foreground">Spam-flagged CIDs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{pacingControl.eligibleLeadsRemaining ?? 0}</div>
              <div className="text-sm text-muted-foreground">Eligible leads</div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                Deferred Missing {pacingControl.deferredMissingCidLeads ?? 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{pacingControl.agentsWithReservedDials ?? 0}</div>
              <div className="text-sm text-muted-foreground">Agents w/ reserved</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-1">Top CIDs</div>
              <div className="text-[11px] text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto">
                {(pacingControl.topCidsByConnects || []).slice(0, 3).map((c) => (
                  <div key={`c-${c.number}`}>
                    {c.number.slice(-4)} · {c.connects} conn
                  </div>
                ))}
                {(pacingControl.topCidsByFails || []).slice(0, 2).map((c) => (
                  <div key={`f-${c.number}`}>
                    {c.number.slice(-4)} · {c.fails} fail
                  </div>
                ))}
                {!pacingControl.topCidsByConnects?.length && !pacingControl.topCidsByFails?.length && (
                  <div>—</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {pacingControl?.dialWindow && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Dial timeline / timezone</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Now ET: {pacingControl.dialWindow.nowEt}
                  {pacingControl.dialWindow.timezoneSource !== 'none'
                    ? ` · source ${pacingControl.dialWindow.timezoneSource.replace(/_/g, ' ')}`
                    : ''}
                </div>
              </div>
              <span
                className={`inline-flex text-xs font-medium px-2 py-1 rounded-full ${
                  pacingControl.dialWindow.idleReason === 'dialing' ||
                  pacingControl.dialWindow.idleReason === 'wave_disabled'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                    : pacingControl.dialWindow.idleReason === 'at_target'
                      ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                }`}
              >
                {pacingControl.dialWindow.idleReasonLabel}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Active zones</div>
                <div className="font-medium">
                  {pacingControl.dialWindow.activeZones.length
                    ? pacingControl.dialWindow.activeZones.join(', ')
                    : pacingControl.dialWindow.etWaveZones == null
                      ? 'None (outside wave)'
                      : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">ET wave</div>
                <div className="font-medium">
                  {pacingControl.dialWindow.etWaveZones?.join(', ') ?? 'Closed'}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Leads in window</div>
                <div className="text-xl font-bold">{pacingControl.dialWindow.eligibleLeadsInWindow}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Eligible total</div>
                <div className="text-xl font-bold">{pacingControl.dialWindow.eligibleLeadsTotal}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {pacingControl && pacingControl.decisionLog?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-sm font-medium mb-2">Pacing decision log</div>
            <div className="max-h-40 overflow-y-auto text-xs space-y-1 font-mono">
              {pacingControl.decisionLog.slice(0, 20).map((e, i) => (
                <div key={`${e.at}-${i}`} className="text-muted-foreground">
                  {new Date(e.at).toLocaleTimeString()} · {e.reason} · {e.fromRatio ?? '?'}→{e.toRatio}
                  {e.shadow ? ' (shadow)' : ''}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className={`text-2xl font-bold ${pcRateColor(stats?.predictiveCancelledRatePct ?? 0)}`}>
              {stats?.predictiveCancelledRatePct ?? 0}%
            </div>
            <div className="text-sm text-muted-foreground">Predictive cancel rate</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">
              No-agent cancels · last 100 · target &lt;3% ideal, 5% max
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats?.pipelineFillPercent ?? 0}%</div>
            <div className="text-sm text-muted-foreground">Pipeline fill</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">
              {stats?.totalActiveCalls ?? 0} / {stats?.targetActiveCalls ?? 0} target
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <span className={`inline-flex text-xs font-semibold px-2 py-0.5 rounded-full ${pacingHealthStyle.className}`}>
              {pacingHealthStyle.label}
            </span>
            <div className="text-sm text-muted-foreground mt-2">Pacing state</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">
              Bridge {formatSeconds(stats?.avgBridgeDelaySeconds ?? 0)} avg
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats?.wrapUpAgents ?? 0}</div>
            <div className="text-sm text-muted-foreground">Wrap-up agents</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">Awaiting disposition — no new dials</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats?.targetActiveCalls ?? 0}</div>
            <div className="text-sm text-muted-foreground">Target active dials</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">available agents × dial ratio</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className={`text-2xl font-bold ${(stats?.pacingDeficit ?? 0) > 0 ? 'text-blue-600 dark:text-blue-400' : ''}`}>
              {stats?.pacingDeficit ?? 0}
            </div>
            <div className="text-sm text-muted-foreground">Pacing deficit</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">Calls needed to refill pipeline</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats?.totalActiveCalls ?? 0}</div>
            <div className="text-sm text-muted-foreground">Active dials</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats?.totalDialedToday ?? 0}</div>
            <div className="text-sm text-muted-foreground">Dialed {rangeLabel}</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">
              {stats?.uniqueLeadsDialedToday ?? 0} unique people
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats?.pickupsToday ?? 0}</div>
            <div className="text-sm text-muted-foreground">Pickup {rangeLabel}</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">Merchants who answered</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats?.connectedToday ?? 0}</div>
            <div className="text-sm text-muted-foreground">Connected {rangeLabel}</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">Agent disposition recorded</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats?.noAnswerToday ?? 0}</div>
            <div className="text-sm text-muted-foreground">No answer {rangeLabel}</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">Timeout / no pickup</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats?.voicemailsFilteredToday ?? 0}</div>
            <div className="text-sm text-muted-foreground">AMD voicemails</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">
              {(stats?.amdHumanToday ?? 0) === 0 && (stats?.voicemailsFilteredToday ?? 0) === 0
                ? 'AMD off — no verdicts'
                : 'Filtered before agent'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats?.userBusyToday ?? 0}</div>
            <div className="text-sm text-muted-foreground">User busy {rangeLabel}</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">Line busy / rejected</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats?.failedToday ?? 0}</div>
            <div className="text-sm text-muted-foreground">Failed {rangeLabel}</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">Technical / carrier failures only</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-500 dark:text-slate-400">{stats?.cancelledToday ?? 0}</div>
            <div className="text-sm text-muted-foreground">Predictive cancelled</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">Another call won the agent — not a failure</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats?.totalOpenSlots ?? 0}</div>
            <div className="text-sm text-muted-foreground">Online open slots</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">Free capacity on online agents</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats?.onlineAgents ?? 0}</div>
            <div className="text-sm text-muted-foreground">Online agents</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats?.activeCampaigns ?? 0}</div>
            <div className="text-sm text-muted-foreground">Active campaigns</div>
          </CardContent>
        </Card>
      </div>

      {activeCampaigns.length > 0 && (
        <Card className="card-elevated">
          <div className="px-5 py-4 border-b border-border bg-muted/50">
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <BoltIcon className="h-5 w-5 text-primary" />
              Campaign pacing
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Active campaigns only ({activeCampaigns.length})
            </p>
          </div>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {activeCampaigns.map((c) => (
                <div key={c.id} className="rounded-lg border border-border/70 p-4 bg-muted/20">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="font-semibold text-sm truncate">{c.name}</p>
                    <div className="flex items-center gap-1 shrink-0">
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          PACING_HEALTH_STYLES[c.pacing?.health ?? 'normal'].className
                        }`}
                      >
                        {PACING_HEALTH_STYLES[c.pacing?.health ?? 'normal'].label}
                      </span>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                      <span>Pipeline</span>
                      <span>{c.pacing?.fillPercent ?? 0}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${Math.min(100, c.pacing?.fillPercent ?? 0)}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <span className="text-muted-foreground">Dial ratio</span>
                    <span className="font-medium text-right">{c.pacing?.dialRatio ?? c.dialRatio}</span>
                    <span className="text-muted-foreground">PC rate</span>
                    <span className={`font-medium text-right ${pcRateColor((c.pacing?.predictiveCancelledRate ?? 0) * 100)}`}>
                      {Math.round((c.pacing?.predictiveCancelledRate ?? 0) * 1000) / 10}%
                    </span>
                    <span className="text-muted-foreground">Available agents</span>
                    <span className="font-medium text-right">{c.pacing?.availableAgents ?? 0}</span>
                    <span className="text-muted-foreground">Active / target</span>
                    <span className="font-medium text-right">
                      {c.pacing?.activeCalls ?? 0} / {c.pacing?.targetActiveCalls ?? 0}
                    </span>
                    <span className="text-muted-foreground">Deficit</span>
                    <span className={`font-medium text-right ${(c.pacing?.deficit ?? 0) > 0 ? 'text-blue-600 dark:text-blue-400' : ''}`}>
                      {c.pacing?.deficit ?? 0}
                    </span>
                    {c.pacing?.fairShareAllowance != null && (
                      <>
                        <span className="text-muted-foreground">Fair-share</span>
                        <span className="font-medium text-right">
                          {c.pacing.fairShareAllowance} dials
                          {c.pacing.performanceWeight != null && c.pacing.performanceWeight !== 1
                            ? ` · wt ${c.pacing.performanceWeight}`
                            : ''}
                        </span>
                      </>
                    )}
                  </div>
                  {c.pacing?.fairSharePeerCount != null && c.pacing.fairSharePeerCount > 1 && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Sharing agents with {c.pacing.fairSharePeerCount} dialable campaigns
                    </p>
                  )}
                  {c.pacing?.isPaused && (
                    <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                      Emergency pause until{' '}
                      {c.pacing.pausedUntil ? new Date(c.pacing.pausedUntil).toLocaleTimeString() : '—'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2 card-elevated">
          <div className="px-5 py-4 border-b border-border bg-muted/50">
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-primary" />
              Agents ({agents.length})
            </h2>
            {/*
              "Available" counts only idle agents, and ringing is broken out on its
              own. An agent with a call ringing at them cannot take another one, and
              counting them here read as free capacity — so the header said
              "1 available" at the same moment the router said "no agents available
              — routing failed", which is where the confusion came from. The router
              subtracts busy agents; this now agrees with it.
            */}
            <p className="text-xs text-muted-foreground mt-0.5">
              {agents.filter((a) => a.callStatus !== 'offline').length} online &middot;{' '}
              {agents.filter((a) => a.callStatus === 'in-call').length} on call &middot;{' '}
              {agents.filter((a) => a.callStatus === 'ringing').length} ringing &middot;{' '}
              {agents.filter((a) => a.callStatus === 'wrap-up').length} wrap-up &middot;{' '}
              {agents.filter((a) => a.callStatus === 'idle').length} available &middot;{' '}
              {agents.filter((a) => a.callStatus === 'offline').length} offline
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground/80">Calling lights:</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden />
                Green — Connected (live with customer)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden />
                Yellow — Dialing / ringing (attempt in progress)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-muted-foreground/20" aria-hidden />
                Grey — Available line
              </span>
            </div>
          </div>
          <CardContent className="p-5">
            {agents.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No agents assigned to auto-dial campaigns.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border/70">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/40 border-b border-border/60">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">Agent</th>
                      <th
                        className="px-3 py-3 text-left font-semibold text-foreground min-w-[10.5rem]"
                        title="Idle time since last dispositioned connect."
                      >
                        Waiting now
                      </th>
                      <th
                        className="px-3 py-3 text-center font-semibold text-blue-700 dark:text-blue-300"
                        title="Green = connected · Yellow = dialing/ringing · Grey = available"
                      >
                        Calling
                      </th>
                      <th className="px-3 py-3 text-center font-semibold text-emerald-700 dark:text-emerald-300" title="Calls this agent bridged and dispositioned (not failed transfer attempts)">Connected</th>
                      <th className="px-3 py-3 text-center font-semibold text-violet-700 dark:text-violet-300">Interested</th>
                      <th className="px-3 py-3 text-center font-semibold text-amber-700 dark:text-amber-300">Follow Up</th>
                      <th className="px-3 py-3 text-center font-semibold text-indigo-700 dark:text-indigo-300">App Out</th>
                      <th className="px-3 py-3 text-center font-semibold text-sky-700 dark:text-sky-300">App In</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...agents]
                      .sort((a, b) => {
                        const priority = (s: AutoDialAgent['callStatus']) =>
                          s === 'in-call' ? 0 : s === 'ringing' ? 1 : s === 'wrap-up' ? 2 : s === 'idle' ? 3 : 4;
                        return priority(a.callStatus) - priority(b.callStatus);
                      })
                      .map((agent) => (
                        <tr
                          key={agent.id}
                          className="border-b border-border/50 last:border-b-0 hover:bg-accent/20 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <div className="h-10 w-10 rounded-full bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center text-sm font-semibold text-primary">
                                  {(agent.aiName || agent.name || 'A').charAt(0).toUpperCase()}
                                </div>
                                <span
                                  className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background ${
                                    agent.callStatus === 'in-call'
                                      ? 'bg-green-500 animate-pulse'
                                      : agent.callStatus === 'ringing'
                                      ? 'bg-yellow-500 animate-pulse'
                                      : agent.callStatus === 'wrap-up'
                                      ? 'bg-purple-500'
                                      : agent.callStatus === 'idle' || agent.isOnline
                                      ? 'bg-green-500'
                                      : 'bg-muted-foreground/40'
                                  }`}
                                  title={
                                    agent.callStatus === 'in-call'
                                      ? 'On call'
                                      : agent.callStatus === 'ringing'
                                      ? 'Ringing'
                                      : agent.callStatus === 'wrap-up'
                                      ? 'Wrap-up'
                                      : agent.callStatus === 'idle' || agent.isOnline
                                      ? 'Online'
                                      : 'Offline'
                                  }
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm md:text-[15px] font-semibold text-foreground truncate">
                                  {agent.aiName || agent.name}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {agent.callStatus === 'wrap-up'
                                    ? `Wrap-up — disposition pending · ${formatWaitTime(agent.wrapUpSeconds)}`
                                    : agent.campaignName ||
                                      agent.calls.find((c) => c.campaignName)?.campaignName ||
                                      '—'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 align-middle">
                            <div className="flex flex-col gap-1 min-w-[9.5rem]">
                              {agent.callStatus === 'idle' ? (
                                <>
                                  <span
                                    className="inline-flex w-fit items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-green-500/15 text-green-700 dark:text-green-300"
                                  >
                                    Online
                                  </span>
                                  <span
                                    className={`text-sm font-semibold tabular-nums leading-none ${
                                      (agent.currentIdleWaitSeconds ?? 0) >= 120
                                        ? 'text-red-600 dark:text-red-400'
                                        : (agent.currentIdleWaitSeconds ?? 0) >= 60
                                          ? 'text-amber-700 dark:text-amber-400'
                                          : 'text-foreground'
                                    }`}
                                  >
                                    {formatWaitTime(agent.currentIdleWaitSeconds ?? 0)}
                                  </span>
                                </>
                              ) : agent.callStatus === 'wrap-up' ||
                                agent.callStatus === 'in-call' ||
                                agent.callStatus === 'ringing' ? (
                                <span
                                  className={`inline-flex w-fit items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                                    agent.callStatus === 'wrap-up'
                                      ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300'
                                      : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                                  }`}
                                >
                                  {agent.callStatus === 'wrap-up' ? 'Wrap-up' : 'On call'}
                                </span>
                              ) : (
                                <span className="inline-flex w-fit items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-muted text-muted-foreground">
                                  Offline
                                </span>
                              )}
                              <div className="grid grid-cols-[4.5rem_1fr] gap-x-2 gap-y-0.5 text-[11px] leading-snug">
                                <span className="text-muted-foreground">Last Contact</span>
                                <span
                                  className="tabular-nums text-foreground/85 whitespace-nowrap"
                                  title="Time since last call with disposition set (connected outcome)"
                                >
                                  {(agent.secondsSinceLastDispositionedConnect ?? 0) > 0
                                    ? `${formatWaitTime(agent.secondsSinceLastDispositionedConnect)} ago`
                                    : '—'}
                                </span>
                                {agent.avgWaitBetweenCallsSeconds > 0 ? (
                                  <>
                                    <span className="text-muted-foreground">Avg wait</span>
                                    <span
                                      className="tabular-nums text-muted-foreground whitespace-nowrap"
                                      title="Average time between dispositioned connects today (not dial-to-dial)"
                                    >
                                      {formatWaitTime(agent.avgWaitBetweenCallsSeconds)}
                                    </span>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="inline-flex flex-col items-center gap-1">
                              <span className="inline-flex min-w-9 justify-center rounded-md border border-blue-200/70 dark:border-blue-800/60 bg-blue-50/80 dark:bg-blue-900/20 px-2 py-0.5 font-semibold text-blue-700 dark:text-blue-300">
                                {agent.activeCalls}
                              </span>
                              <div className="flex gap-1">
                                {Array.from({ length: agent.maxCalls }).map((_, i) => (
                                  <span
                                    key={i}
                                    className={`h-2 w-2 rounded-full ${
                                      i < agent.bridgedCalls
                                        ? 'bg-green-500'
                                        : i < agent.activeCalls
                                        ? 'bg-amber-400'
                                        : 'bg-muted-foreground/20'
                                    }`}
                                    title={
                                      i < agent.bridgedCalls
                                        ? `Line ${i + 1}: Connected (live with customer)`
                                        : i < agent.activeCalls
                                        ? `Line ${i + 1}: Dialing / ringing`
                                        : `Line ${i + 1}: Available`
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="inline-flex min-w-9 justify-center rounded-md border border-emerald-200/70 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-900/20 px-2 py-0.5 font-semibold text-emerald-700 dark:text-emerald-300">
                              {agent.dailyCounters.answeredCalls}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="inline-flex min-w-9 justify-center rounded-md border border-violet-200/70 dark:border-violet-800/60 bg-violet-50/80 dark:bg-violet-900/20 px-2 py-0.5 font-semibold text-violet-700 dark:text-violet-300">
                              {agent.dailyCounters.interested}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="inline-flex min-w-9 justify-center rounded-md border border-amber-200/70 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-900/20 px-2 py-0.5 font-semibold text-amber-700 dark:text-amber-300">
                              {agent.dailyCounters.followUps}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="inline-flex min-w-9 justify-center rounded-md border border-indigo-200/70 dark:border-indigo-800/60 bg-indigo-50/80 dark:bg-indigo-900/20 px-2 py-0.5 font-semibold text-indigo-700 dark:text-indigo-300">
                              {agent.dailyCounters.appOut}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="inline-flex min-w-9 justify-center rounded-md border border-sky-200/70 dark:border-sky-800/60 bg-sky-50/80 dark:bg-sky-900/20 px-2 py-0.5 font-semibold text-sky-700 dark:text-sky-300">
                              {agent.dailyCounters.appIn}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot className="bg-muted/30 border-t border-border/60">
                    <tr>
                      <td className="px-4 py-2 text-xs font-medium text-muted-foreground" colSpan={3}>
                        Dispositioned connects ({rangeLabel})
                      </td>
                      <td className="px-3 py-2 text-center text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                        {stats?.connectedToday ?? 0}
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-muted-foreground" colSpan={4}>
                        Per-agent columns may sum higher when calls transferred between agents
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold mb-3">Activity log</h2>
            <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
              {activityLog.length === 0 ? (
                <p className="text-xs text-muted-foreground">Waiting for dial events…</p>
              ) : (
                activityLog.map((entry) => (
                  <div key={entry.id} className="text-xs border-b border-border/50 pb-2">
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-mono">
                      <span>{new Date(entry.at).toLocaleTimeString()}</span>
                      {entry.type && (
                        <span className="rounded bg-muted px-1.5 py-0.5 font-sans text-[9px] uppercase tracking-wide">
                          {ACTIVITY_TYPE_LABELS[entry.type] || entry.type}
                        </span>
                      )}
                    </div>
                    <div className={`font-medium ${ACTIVITY_LEVEL_STYLES[entry.level]}`}>
                      {entry.message}
                    </div>
                    {(entry.leadName || entry.leadPhone || entry.agentName) && (
                      <div className="text-muted-foreground mt-0.5">
                        {[entry.leadName || entry.leadPhone, entry.agentName].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lead Status Breakdown (same layout as manual Live Monitor) */}
      <Card className="card-elevated">
        <div className="px-5 py-4 border-b border-border bg-muted/50">
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-primary" />
            Lead Status Breakdown
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Outcomes for {rangeLabel}</p>
        </div>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">{stats?.totalDialedToday ?? 0}</div>
              <div className="text-xs font-medium text-muted-foreground mt-1">Total Calls</div>
              <div className="text-[11px] text-muted-foreground/80">
                {stats?.uniqueLeadsDialedToday ?? 0} unique people · {stats?.connectedToday ?? 0} dispositioned
              </div>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">
                {(stats?.connectedToday ?? 0) > 0
                  ? `1:${Math.max(Math.round((stats?.totalDialedToday ?? 0) / (stats?.connectedToday ?? 1)), 1)}`
                  : '1:0'}
              </div>
              <div className="text-xs font-medium text-muted-foreground mt-1">Call-to-Answer Ratio</div>
              <div className="text-[11px] text-muted-foreground/80">agent dispositions per attempts</div>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">
                {formatWaitTime(stats?.avgWaitToNextCallSeconds ?? 0)}
              </div>
              <div className="text-xs font-medium text-muted-foreground mt-1">Avg gap between calls</div>
              <div className="text-[11px] text-muted-foreground/80">day average · not live idle</div>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">
                {formatSeconds(stats?.connectedAvgDuration ?? 0)}
              </div>
              <div className="text-xs font-medium text-muted-foreground mt-1">Avg Connected Call Duration</div>
              <div className="text-[11px] text-muted-foreground/80">dispositioned calls only</div>
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(LEAD_STATUS_LABELS).map(([key, meta]) => {
              const count = leadCounts[key] ?? 0;
              const total = Math.max(Number(leadCounts.total || 0), 1);
              const pct = Math.round((count / total) * 100);
              return (
                <div key={key} className="flex items-center gap-3">
                  <div className="w-28 flex-shrink-0">
                    <span className={`inline-flex text-xs px-2 py-0.5 rounded-full font-medium ${meta.color}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${meta.color.split(' ')[0]}`}
                      style={{ width: `${Math.max(pct, 1)}%` }}
                    />
                  </div>
                  <div className="w-16 text-right">
                    <span className="text-sm font-semibold text-foreground">{count}</span>
                    <span className="text-xs text-muted-foreground ml-1">({pct}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <SignalIcon className="h-5 w-5" />
          Live call feed
        </h2>
        <LiveCallsSection autoDialerOnly />
      </div>
    </div>
  );
};

export default AutoDialMonitorPage;
